import { useNavigate } from 'react-router-dom';

interface ProductCardProps {
  product: {
    id: string;
    name?: string;
    title?: string;
    handle: string;
    description: string;
    image: string;
    price: number;
    originalPrice?: number;
    compareAtPrice?: number;
    currencyCode: string;
    availableForSale: boolean;
  };
  viewMode?: string;
  onAddToCart?: (product: any, color: string, size: string, quantity: number) => void;
}

export default function ProductCard({ product, viewMode = 'grid' }: ProductCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/product/${product.handle}`);
  };

  // Use name or title, whichever is available
  const productTitle = product.name || product.title || 'Untitled Product';
  const comparePrice = product.compareAtPrice || product.originalPrice;

  // List view layout
  if (viewMode === 'list') {
    return (
      <div 
        onClick={handleClick}
        className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer group flex"
      >
        <div className="relative overflow-hidden w-64 flex-shrink-0">
          <img 
            src={product.image} 
            alt={productTitle}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
          {comparePrice && comparePrice > product.price && (
            <span className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold whitespace-nowrap">
              Save {product.currencyCode} ${(comparePrice - product.price).toFixed(2)}
            </span>
          )}
          {!product.availableForSale && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold whitespace-nowrap">
                Out of Stock
              </span>
            </div>
          )}
        </div>
        <div className="p-6 flex-1 flex flex-col justify-between">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{productTitle}</h3>
            <p className="text-gray-600 mb-4">{product.description}</p>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-cyan-700">
                {product.currencyCode} ${product.price.toFixed(2)}
              </span>
              {comparePrice && comparePrice > product.price && (
                <span className="text-lg text-gray-400 line-through">
                  ${comparePrice.toFixed(2)}
                </span>
              )}
            </div>
            <button 
              className="bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900 text-white px-6 py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Grid view layout (default)
  return (
    <div 
      onClick={handleClick}
      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer group"
    >
      <div className="relative overflow-hidden">
        <img 
          src={product.image} 
          alt={productTitle}
          className="w-full h-64 object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
        {comparePrice && comparePrice > product.price && (
          <span className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold whitespace-nowrap">
            Save {product.currencyCode} ${(comparePrice - product.price).toFixed(2)}
          </span>
        )}
        {!product.availableForSale && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold whitespace-nowrap">
              Out of Stock
            </span>
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">{productTitle}</h3>
        <p className="text-gray-600 mb-4 line-clamp-2">{product.description}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-cyan-700">
              {product.currencyCode} ${product.price.toFixed(2)}
            </span>
            {comparePrice && comparePrice > product.price && (
              <span className="text-lg text-gray-400 line-through">
                ${comparePrice.toFixed(2)}
              </span>
            )}
          </div>
          <button 
            className="bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900 text-white px-4 py-2 rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
}
